// This module manages a singleton WebSocket connection for live tracking, sos triggers.
// only one connection is open at a time and allows multiple components to subscribe to vehicle tracking and sos triggers.

let socket = null;
let intentionalClose = false;
let reconnectTimer = null;
let reconnectAttempts = 0;
let lastRole = null;
let lastConnectionId = null;
let stabilityTimer = null;       // resets backoff only after the socket stays open a while
let lastMessageAt = 0;           // epoch ms of the last received message (staleness watchdog)
let receivedSinceOpen = false;   // has a real message arrived on the current connection?
const MAX_RECONNECT_ATTEMPTS = 50;
const STABILITY_MS = 5000;       // how long a connection must stay open before backoff resets
const subscribers = new Set();
const reconnectCallbacks = new Set(); // notified after each successful (re)open

const wssUrls = {
  CORPORATEADMIN: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  EMPLOYEE: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  SUPERADMIN: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  TENANTADMIN: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  VENDOR: process.env.REACT_APP_VENDOR_WEBSOCKET_URL,
  PUBLIC: process.env.REACT_APP_PUBLIC_WEBSOCKET_URL,
};

const wssKeys = {
  VENDOR: 'vendorId',
  CORPORATEADMIN: 'corporateId',
  EMPLOYEE: 'corporateId',
  SUPERADMIN: 'corporateId',
  TENANTADMIN: 'corporateId',
  PUBLIC: 'tripId'
}

function getReconnectDelay() {
  // Exponential backoff (1s, 2s, 4s, ... capped at 30s) with jitter so a
  // flapping/accept-then-drop server doesn't get hammered in lockstep. (FR-007)
  const base = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
  return Math.round(base * (0.5 + Math.random() * 0.5));
}

function scheduleReconnect() {
  if (intentionalClose || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
  const delay = getReconnectDelay();
  reconnectTimer = setTimeout(() => {
    reconnectAttempts++;
    connect(lastRole, lastConnectionId);
  }, delay);
}

// establishes a WebSocket connection
function connect(role, connectionId) {

  if (socket && socket.readyState === WebSocket.OPEN) {
    return;
  }

  // Clean up any existing broken socket
  if (socket) {
    try { socket.close(); } catch (_) {}
    socket = null;
  }

  const wssUrl = wssUrls[role] || wssUrls["CORPORATEADMIN"];
  const key = wssKeys[role] || wssKeys["CORPORATEADMIN"];

  if (!wssUrl) {
    console.error('WebSocket URL not found for role:', role);
    return;
  }

  // Store for reconnection
  lastRole = role;
  lastConnectionId = connectionId;
  intentionalClose = false;

  // For all non-public connections, pass the access token as a query param.
  // Only in development mode.
  let connectUrl = wssUrl;
  if (role !== 'PUBLIC') {
    const token = localStorage.getItem('token');
    if (token) {
      connectUrl = `${wssUrl}?token=${token}`;
    }
  }

  socket = new WebSocket(connectUrl);

  socket.onopen = () => {
    console.log('WebSocket connection established: ', wssUrl);
    lastMessageAt = Date.now();
    receivedSinceOpen = false; // fresh connection — no real traffic yet

    // Reset backoff only once the connection has proven stable (FR-008), so a
    // server that accepts then immediately drops us backs off instead of looping.
    if (stabilityTimer) clearTimeout(stabilityTimer);
    stabilityTimer = setTimeout(() => { reconnectAttempts = 0; }, STABILITY_MS);

    const message = {
      type: 'subscribe',
      [key]: connectionId
    };

    socket.send(JSON.stringify(message));

    // Notify listeners so they can re-pull authoritative state after a reconnect (FR-010).
    reconnectCallbacks.forEach((cb) => {
      try { cb(); } catch (_) { /* ignore listener errors */ }
    });
  };

  socket.onmessage = (event) => {
    try {
      lastMessageAt = Date.now();
      receivedSinceOpen = true; // real traffic seen on this connection
      const data = JSON.parse(event.data);

      // console.log('WebSocket data:', data);

      // Notify all subscribers of the new data.
      subscribers.forEach((callback) => callback(data));
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  socket.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  socket.onclose = (event) => {
    if (stabilityTimer) { clearTimeout(stabilityTimer); stabilityTimer = null; }
    console.log(
      `WebSocket connection closed: ${wssUrl} (code=${event?.code}, reason="${event?.reason || 'none'}", wasClean=${event?.wasClean})`
    );
    socket = null;
    // Auto-reconnect unless disconnect() was called intentionally
    if (!intentionalClose) {
      scheduleReconnect();
    }
  };
}

// disconnects and cleans up the WebSocket connection
function disconnect() {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (stabilityTimer) {
    clearTimeout(stabilityTimer);
    stabilityTimer = null;
  }
  reconnectAttempts = 0;
  if (socket) {
    console.log('Disconnecting WebSocket connection...');
    socket.close();
    socket = null;
  }

  // Clear all subscribers
  subscribers.clear();
  console.log('All WebSocket subscribers cleared');
}

// gets the current connection status
function getConnectionStatus() {
  if (!socket) {
    return { connected: false, readyState: null, subscribers: subscribers.size };
  }

  return {
    connected: socket.readyState === WebSocket.OPEN,
    readyState: socket.readyState,
    subscribers: subscribers.size
  };
}

// Sends a message on the live socket if it is open. Returns false when it is not, so a
// caller can wait for onReconnect rather than assume the message went out.
function send(message) {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return false;
  }
  try {
    socket.send(JSON.stringify(message));
    return true;
  } catch (_) {
    return false;
  }
}

// the id this socket subscribed with (corporateId, vendorId or tripId by role), so a
// consumer can address a request without resolving the user again.
function getConnectionId() {
  return lastConnectionId;
}

// subscribes a component to WebSocket data updates.
function subscribe(callback) {
  subscribers.add(callback);
}

// unsubscribes a component from WebSocket data updates.
function unsubscribe(callback) {
  subscribers.delete(callback);
}

// registers a callback fired after each successful (re)connection (FR-010).
function onReconnect(callback) {
  reconnectCallbacks.add(callback);
}

// removes a reconnect callback.
function offReconnect(callback) {
  reconnectCallbacks.delete(callback);
}

// epoch ms of the last message received (for the staleness watchdog, FR-011).
function getLastMessageAt() {
  return lastMessageAt;
}

// True only if the connection HAD traffic and then went silent past the threshold.
// A connection that has simply been quiet since opening (no active trips / online
// drivers, no server heartbeat) is NOT considered stale, so we don't force pointless
// reconnect+refetch loops during quiet periods (FR-011).
function isStreamStale(thresholdMs) {
  return receivedSinceOpen && lastMessageAt > 0 && Date.now() - lastMessageAt > thresholdMs;
}

// force-closes the live socket to trigger the auto-reconnect path without marking
// the close intentional (used by the staleness watchdog for half-open sockets, FR-011).
function forceReconnect() {
  if (socket) {
    try { socket.close(); } catch (_) { /* ignore */ }
  }
}

export {
  connect,
  disconnect,
  getConnectionStatus,
  send,
  getConnectionId,
  subscribe,
  unsubscribe,
  onReconnect,
  offReconnect,
  getLastMessageAt,
  isStreamStale,
  forceReconnect,
};
